import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  MessageSquare,
  Reply,
  Check,
  CalendarPlus,
  PenLine,
  Volume2,
  Timer,
  ArrowDown,
} from "lucide-react";
import { CONFIRMATION_VIDEO_URL } from "@/config";

const STORAGE_KEY = "appointext_checklist_steps";

const STEPS = [
  {
    id: "inbox",
    icon: MessageSquare,
    title: "Keep an eye on your email and texts",
    desc: "We've sent your join link to both. If you don't watch now, that's where you'll find it.",
    hasCalendar: true,
    testId: "step-inbox",
  },
  {
    id: "number",
    icon: Reply,
    title: "Save our number",
    desc: "So the reminder text doesn't land from an unknown number and get ignored.",
    testId: "step-number",
  },
  {
    id: "setter",
    icon: Check,
    title: "Reply SETTER to that text",
    desc: "Not required, but it opens the thread so you can ask questions during the session and get a real answer back.",
    testId: "step-setter",
  },
];

const AGENDA = [
  "Why the setter seat decides your revenue",
  "What running setters actually costs you in a year",
  "The four non-negotiables and which can be trained",
  "Your three options honestly compared",
];

const TIPS = [
  { icon: Timer, text: "Plan for the full 60 minutes." },
  { icon: Volume2, text: "Join from somewhere quiet, not while driving." },
  { icon: PenLine, text: "Bring a pen and paper. You'll want to take notes." },
];

function icsTimestamp(date) {
  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

function Nav() {
  return (
    <header
      data-testid="nav-strip"
      className="border-b border-[#26262A] bg-[#0A0A0B] h-16 flex items-center px-6 lg:px-8"
    >
      <span
        data-testid="nav-brand"
        className="font-display text-lg font-extrabold tracking-tight text-[#F4F4F2]"
      >
        Appointext<span className="text-[#E8B33C]">.ai</span>
      </span>
    </header>
  );
}

function Hero() {
  return (
    <section data-testid="hero-section" className="pt-16 pb-10 text-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto"
      >
        <p
          data-testid="hero-pre-title"
          className="text-sm font-bold uppercase tracking-[0.15em] text-[#E8B33C]"
        >
          You're Registered
        </p>
        <h1
          data-testid="hero-title"
          className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-[#F4F4F2] leading-tight"
        >
          You're in.
        </h1>
        <p
          data-testid="hero-subhead"
          className="mt-6 text-base sm:text-lg text-[#8A8A86] leading-relaxed max-w-xl mx-auto"
        >
          Your join link is on its way to your email and your phone, so you can
          come back to it any time.
        </p>
        <p
          data-testid="hero-video-pointer"
          className="mt-6 inline-flex items-center gap-2 rounded-full border-[0.5px] border-[#E8B33C]/40 bg-[#141416] px-5 py-2.5 text-sm font-semibold text-[#F4F4F2]"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E8B33C] opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#E8B33C]" />
          </span>
          Start with Haroon's video below
          <ArrowDown className="h-4 w-4 text-[#E8B33C] animate-bounce" aria-hidden="true" />
        </p>
      </motion.div>
    </section>
  );
}

function VideoBlock() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const unmute = () => {
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      v.volume = 1;
      v.play().catch(() => {});
      setMuted(false);
    }
  };

  return (
    <section data-testid="video-section" className="px-4 mt-12">
      <div className="max-w-[720px] mx-auto text-center">
        <p
          data-testid="video-eyebrow"
          className="text-sm font-bold uppercase tracking-[0.15em] text-[#E8B33C]"
        >
          Watch This First
        </p>
        <p data-testid="video-caption" className="mt-2 text-sm text-[#8A8A86]">
          Haroon recorded this for people who've just registered — your confirmation
          and housekeeping, in one short video.
        </p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-5 relative aspect-video w-full rounded-xl border-2 border-[#E8B33C] bg-[#141416] overflow-hidden"
        >
          <video
            ref={videoRef}
            data-testid="confirmation-video"
            poster="/assets/workshop-poster.jpg"
            autoPlay
            muted
            playsInline
            controls
            preload="auto"
            className="h-full w-full object-cover"
          >
            <source src={CONFIRMATION_VIDEO_URL} type="video/mp4" />
            <source src="/assets/workshop-confirmation-web.webm" type="video/webm" />
          </video>
          {muted && (
            <button
              type="button"
              data-testid="video-unmute-button"
              onClick={unmute}
              className="absolute bottom-16 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-[#E8B33C] px-5 py-2.5 text-sm font-bold text-[#0A0A0B] transition-colors duration-200 hover:bg-[#d9a52f] focus:outline-none focus:ring-2 focus:ring-[#E8B33C] focus:ring-offset-2 focus:ring-offset-[#0A0A0B]"
            >
              <Volume2 className="h-4 w-4" aria-hidden="true" />
              Playing — tap for sound
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
}

function ChecklistItem({ step, index, done, onToggle, onAddToCalendar }) {
  const Icon = step.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <div
        data-testid={step.testId}
        role="checkbox"
        aria-checked={done}
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onToggle();
          }
        }}
        className={`flex items-start gap-4 p-6 border-[0.5px] rounded-xl cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#E8B33C] focus:ring-offset-2 focus:ring-offset-[#0A0A0B] ${
          done
            ? "border-[#E8B33C]/40 bg-[#141416]"
            : "border-[#26262A] bg-[#141416] hover:border-[#3a3a40]"
        }`}
      >
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-mono text-sm font-bold transition-colors duration-200 ${
            done ? "bg-[#E8B33C] text-[#0A0A0B]" : "bg-[#0A0A0B] text-[#E8B33C]"
          }`}
          aria-hidden="true"
        >
          {done ? <Check className="h-5 w-5" /> : `0${index + 1}`}
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-[#8A8A86]" aria-hidden="true" />
            <h3 className="text-base font-bold tracking-tight text-[#F4F4F2] sm:text-lg">
              {step.title}
            </h3>
          </div>
          <p className="mt-2 text-sm sm:text-base leading-relaxed text-[#8A8A86]">{step.desc}</p>
          {step.hasCalendar && (
            <button
              type="button"
              data-testid="add-to-calendar-button"
              onClick={(e) => {
                e.stopPropagation();
                onAddToCalendar();
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className="mt-4 inline-flex items-center gap-2 rounded-full border-[0.5px] border-[#26262A] bg-[#0A0A0B] px-4 py-2 text-sm font-semibold text-[#F4F4F2] transition-colors duration-200 hover:border-[#E8B33C]/50 focus:outline-none focus:ring-2 focus:ring-[#E8B33C]"
            >
              <CalendarPlus className="h-4 w-4 text-[#E8B33C]" aria-hidden="true" />
              Add to calendar
            </button>
          )}
        </div>
        <span
          data-testid={`${step.testId}-status`}
          className={`mt-1 shrink-0 rounded-full px-3 py-1 text-xs font-bold ${
            done ? "bg-[#E8B33C] text-[#0A0A0B]" : "bg-[#0A0A0B] text-[#8A8A86]"
          }`}
        >
          {done ? "Done" : "To do"}
        </span>
      </div>
    </motion.div>
  );
}

function Checklist() {
  const [completed, setCompleted] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  });
  const pageLoadTime = useMemo(() => new Date(), []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
  }, [completed]);

  const toggle = (id) => setCompleted((c) => ({ ...c, [id]: !c[id] }));
  const doneCount = STEPS.filter((s) => completed[s.id]).length;
  const pct = Math.round((doneCount / STEPS.length) * 100);

  const addToCalendar = () => {
    const start = new Date(pageLoadTime.getTime() + 15 * 60 * 1000);
    const end = new Date(start.getTime() + 70 * 60 * 1000);
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Appointext.ai//The Setter Problem Workshop//EN",
      "BEGIN:VEVENT",
      `UID:${Date.now()}@appointext.ai`,
      `DTSTAMP:${icsTimestamp(new Date())}`,
      `DTSTART:${icsTimestamp(start)}`,
      `DTEND:${icsTimestamp(end)}`,
      "SUMMARY:The Setter Problem — High-Ticket Sales & Closing Workshop",
      "DESCRIPTION:Your join link is in your email and your texts from Appointext.ai.",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "setter-workshop.ics";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section data-testid="checklist-section" className="max-w-3xl mx-auto px-4 sm:px-6 mt-24">
      <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#E8B33C] text-center">
        So You Don't Miss It
      </p>
      <h2
        data-testid="checklist-header"
        className="mt-3 text-center font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#F4F4F2]"
      >
        Three things worth doing now
      </h2>
      <p data-testid="checklist-intro" className="mt-4 text-center text-base text-[#8A8A86]">
        You'll get the link either way. These just make sure it doesn't get buried.
      </p>

      <div className="mt-8">
        <div className="flex items-center justify-between text-sm font-semibold">
          <span data-testid="progress-label" className="text-[#F4F4F2]">
            {doneCount} of 3 ticked off
          </span>
        </div>
        <div
          data-testid="progress-bar-track"
          className="mt-2 h-1.5 w-full rounded-full bg-[#26262A]"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <motion.div
            data-testid="progress-bar-fill"
            className="h-1.5 rounded-full bg-[#E8B33C]"
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4 mt-8">
        {STEPS.map((step, i) => (
          <ChecklistItem
            key={step.id}
            step={step}
            index={i}
            done={!!completed[step.id]}
            onToggle={() => toggle(step.id)}
            onAddToCalendar={addToCalendar}
          />
        ))}
      </div>

      {doneCount === 3 && (
        <motion.p
          data-testid="all-done-message"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-xl border-[0.5px] border-[#26262A] bg-[#141416] p-5 text-center text-base font-semibold text-[#F4F4F2]"
        >
          All three done. See you inside.
        </motion.p>
      )}
    </section>
  );
}

function Agenda() {
  return (
    <section data-testid="agenda-section" className="py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#E8B33C]">
          One Session. Zero Fluff.
        </p>
        <h2
          data-testid="agenda-header"
          className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#F4F4F2]"
        >
          What You'll Discover
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12">
          {AGENDA.map((item, i) => (
            <motion.div
              key={item}
              data-testid={`agenda-item-${i + 1}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="flex items-start gap-5 rounded-xl border-[0.5px] border-[#26262A] bg-[#141416] p-8"
            >
              <span
                aria-hidden="true"
                className="shrink-0 font-mono text-lg font-bold text-[#E8B33C]"
              >
                {`0${i + 1}`}
              </span>
              <p className="text-base leading-relaxed text-[#F4F4F2]">{item}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Tips() {
  return (
    <section data-testid="tips-section" className="py-24 border-t-[0.5px] border-[#26262A]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#E8B33C]">
          Set Yourself Up
        </p>
        <h2
          data-testid="tips-header"
          className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#F4F4F2]"
        >
          Before You Join
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
          {TIPS.map(({ icon: Icon, text }, i) => (
            <div
              key={text}
              data-testid={`tip-${i + 1}`}
              className="rounded-xl border-[0.5px] border-[#26262A] bg-[#141416] p-8"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0A0A0B] border-[0.5px] border-[#26262A]">
                <Icon className="h-5 w-5 text-[#E8B33C]" aria-hidden="true" />
              </span>
              <p className="mt-4 text-base leading-relaxed text-[#F4F4F2]">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Host() {
  return (
    <section data-testid="host-section" className="py-24 border-t-[0.5px] border-[#26262A]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="bg-[#141416] border-[0.5px] border-[#26262A] rounded-2xl p-8 lg:p-12 flex flex-col md:flex-row gap-8 lg:gap-12 items-center">
          <img
            data-testid="host-photo"
            src="https://setterworkshop.appointext.ai/assets/haroon.png"
            alt="Haroon Naseer, host of The Setter Problem workshop"
            className="h-44 w-44 shrink-0 rounded-xl border-[0.5px] border-[#26262A] object-cover"
          />
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#E8B33C]">
              Your Host
            </p>
            <h2
              data-testid="host-header"
              className="mt-2 font-display text-xl sm:text-2xl font-bold tracking-tight text-[#F4F4F2]"
            >
              Hosted by Haroon Naseer
            </h2>
            <p data-testid="host-bio" className="mt-4 text-base leading-relaxed text-[#8A8A86]">
              Ten years in high-ticket sales. Started as a setter on 1% commission, and has
              personally closed over 400 clients.
            </p>
            <div className="mt-6 flex flex-wrap gap-8">
              <div data-testid="host-stat-years">
                <p className="font-mono text-2xl font-bold text-[#E8B33C]">10 yrs</p>
                <p className="text-sm text-[#8A8A86]">In high-ticket sales</p>
              </div>
              <div data-testid="host-stat-clients">
                <p className="font-mono text-2xl font-bold text-[#E8B33C]">400+</p>
                <p className="text-sm text-[#8A8A86]">Clients closed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      data-testid="footer"
      className="border-t-[0.5px] border-[#26262A] py-8 text-center"
    >
      <p className="font-display text-sm font-bold text-[#F4F4F2]">
        Appointext<span className="text-[#E8B33C]">.ai</span>
      </p>
      <p className="mt-1 text-xs text-[#8A8A86]">
        The Setter Problem — High-Ticket Sales &amp; Closing Workshop
      </p>
    </footer>
  );
}

function ThankYouPage() {
  // Fire the Lead event exactly once per session — this page only renders
  // after a genuine registration, so it is the real conversion signal.
  useEffect(() => {
    if (window.fbq && !sessionStorage.getItem("lead_fired")) {
      window.fbq("track", "Lead");
      sessionStorage.setItem("lead_fired", "1");
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0B] font-sans text-[#F4F4F2] antialiased">
      <Nav />
      <Hero />
      <VideoBlock />
      <Checklist />
      <Agenda />
      <Tips />
      <Host />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<ThankYouPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
